<template>
  <div
    class="company-select"
    :class="{ 'company-select--subscription': stripeSubscriptionUiEnabled && $store.getters['company/showSubscriptionQuota'] }"
  >
    <v-select
      ref="select"
      class="shadow"
      :value="selectedCompany"
      return-object
      :items="filteredCompanies"
      item-text="name"
      :hide-details="true"
      dense
      outlined
      rounded
      item-value="id"
      append-icon="icon-arrow-down"
      full-width
      :menu-props="{
        contentClass: 'company-select-menu',
        positionY: 20
      }"
      @input="setCompany"
    >
      <template #selection="{item}">
        <div class="company-selection">
          <div class="d-flex align-center">
            <v-icon class="mr-2" color="primary">icon-folder</v-icon>
            <span class="cut-text">
              {{ getLabel(item) }}
            </span>
          </div>
          <div class="company-facilities-count">
            <CompanyFacilitiesCounts v-if="stripeSubscriptionUiEnabled" :company="item" />
            <template v-else>
              <FacilitiesCount :isOwner="true" class="mx-3" />
              <FacilitiesCount :isOwner="false" />
            </template>
          </div>
        </div>
      </template>

      <template #prepend-item>
        <v-text-field
          v-model="searchQuery"
          :placeholder="$t('message.common.searchCompany')"
          prepend-inner-icon="icon-search"
          outlined
          rounded
          class="my-2 px-4"
          :hide-details="true"
        ></v-text-field>
      </template>

      <template #item="{item}">
        <div class="company-selection">
          <div class="py-2">
            {{ getLabel(item) }}
          </div>
          <div class="company-facilities-count">
            <CompanyFacilitiesCounts v-if="stripeSubscriptionUiEnabled" :company="item" />
            <template v-else>
              <FacilitiesCount :isOwner="true" :company="item" class="mx-3" />
              <FacilitiesCount :isOwner="false" :company="item" />
            </template>
          </div>
        </div>
      </template>

      <template #append-item>
        <template v-if="canViewTeam || canAddCompany">
          <v-divider class="mb-2"></v-divider>
          <v-list-item>
            <div
              class="d-flex flex-row align-center justify-space-between w-100"
            >
              <ui-button
                v-if="canAddCompany"
                color="primary"
                :to="{ name: 'wizard', query: { action: 'add' } }"
                @click="$refs.select.blur()"
              >
                {{ $t("message.permissions.addCompanyAsOwner") }}
              </ui-button>
              <v-spacer></v-spacer>
              <ui-button
                v-if="canViewTeam"
                outlined
                shadow
                icon="mdi-tune-vertical"
                :to="{ name: 'permissions' }"
                @click="$refs.select.blur()"
              >
                {{ $t("message.permissions.team") }}
              </ui-button>
            </div>
          </v-list-item>
        </template>
      </template>
    </v-select>
  </div>
</template>

<script>
import CompanyFacilitiesCounts from "./CompanyFacilitiesCounts.vue";
import FacilitiesCount from "./FacilitiesCount.vue";

import { mapGetters, mapState } from "vuex";

import { pluralCount } from "@/helpers/helpers";
import { permissionsInfo } from "@/helpers/mixins/permissions-info";
import { GTM_EVENT_NAME } from "@/enums/gtm-events";
import { LOCAL_STORAGE_KEYS } from "@/enums/local-storage";
import * as Sentry from "@sentry/vue";
import { moduleCheck } from "@/helpers/module-check";
import registerAnalyticsGroup from "@/helpers/register-analytics-group";
import usersService from "@/services/users";
import serviceHandler from "@/helpers/handlers/service";
import companyService from "@/services/company";

export default {
  name: "CompanySelect",
  data() {
    return {
      searchQuery: ""
    };
  },
  mixins: [permissionsInfo],
  components: { CompanyFacilitiesCounts, FacilitiesCount },
  computed: {
    ...mapState({
      companies: state => state.company.companies,
      selectedCompany: state => state.company.company
    }),
    ...mapGetters({
      stripeSubscriptionUiEnabled: "settings/stripeSubscriptionUiEnabled",
      canAddCompany: "company/canAddCompany"
    }),
    filteredCompanies() {
      return this.companies.filter(
        company =>
          company &&
          company.name &&
          (company.name
            .toLowerCase()
            .includes(this.searchQuery.toLowerCase()) ||
            company.id === (this.selectedCompany && this.selectedCompany.id))
      );
    }
  },
  methods: {
    async setCompany(selectedCompany) {
      this.$store.commit("company/SET_LOADING", true);
      try {
        this.searchQuery = "";
        this.$store.commit("company/SET_COMPANY", null);
        this.$store.commit("company/SET_COMPANY_ID", null);
        localStorage.setItem(LOCAL_STORAGE_KEYS.COMPANY_ID, selectedCompany.id);

        const company = await serviceHandler(
          companyService.getCompanyDetails(selectedCompany.id)
        );
        const {
          id: company_id,
          is_paying: company_is_paying,
          is_testing: company_is_testing
        } = company;
        const { user_id } = company.company_user;

        Sentry.setUser({ id: user_id });

        localStorage.setItem(LOCAL_STORAGE_KEYS.COMPANY_ID, company.id);
        const { company_subscription } = await serviceHandler(
          usersService.getUserDetails()
        );
        await this.$store.dispatch(
          "auth/setPlanRestrictions",
          company_subscription
        ); // Update plan restrictions for the new company before rendering components that depend on it
        this.$store.dispatch("company/setCompany", {
          company
        });
        this.$store.dispatch("externalChanges/fetchProtectionSummary");

        registerAnalyticsGroup(this.$store.state.company);

        this.$gtm.trackCustomEvent({
          event: GTM_EVENT_NAME.COMPANY_SELECT,
          company_id
        });
        this.$gtm.trackCustomEvent({
          event: GTM_EVENT_NAME.SET_USER_DATA,
          company_id,
          user_id,
          company_is_paying,
          company_is_testing
        });
        if (this.$route.query && this.$route.query.companyId) {
          const query = Object.assign({}, this.$route.query);
          delete query["companyId"];
          this.$router.replace({ query }).catch(_err => {});
        }
        if (
          this.$route.meta.hasOwnProperty("module") &&
          !moduleCheck(this.$route.meta.module)
        ) {
          this.$router.replace({ name: "dashboard" });
        } else {
          this.$router.push({ name: "dashboard" });
        }
      } finally {
        this.$store.commit("company/SET_LOADING", false);
      }
    },
    getLabel(company) {
      const { type } = company.company_user;
      return `${company.name} (${this.$t(
        `message.permissions.accountTypes.${type}`
      )})`;
    },
    pluralCount
  }
};
</script>

<style lang="scss">
.company-select {
  max-width: 610px;
  width: 100%;
  margin-left: $spacing * 2.5;

  .v-input__slot {
    min-height: 46px !important;
  }

  .v-input__append-inner {
    margin-top: auto !important;
    margin-bottom: auto !important;
  }
}

.company-select .company-selection,
.company-select-menu .company-selection {
  max-width: 548px;
  font-weight: 500;
  @include font-size(14px);
  line-height: 17px;
  color: $text;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.company-select-menu .company-selection {
  max-width: 578px;
}

.cut-text {
  width: 330px;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
}

.company-selection .company-facilities-count {
  display: flex;
  align-items: center;
  margin-left: $spacing * 2.5;
  margin-right: $spacing * 1.5;
}

.company-select-menu {
  border: 1px solid rgba($text, 0.08);
  border-radius: 25px;
}
</style>

<style scoped>
.company-select--subscription .company-selection > .d-flex,
.company-select--subscription .company-selection > .py-2 {
  min-width: 0;
  flex: 1;
}
.company-select--subscription .company-selection .cut-text {
  width: auto;
}
.company-select--subscription .company-facilities-count {
  flex-shrink: 0;
}
</style>
